import {
  type ExpiredSharedTechnicalDataPackage,
  type InterrogationStatus,
  type PartFileViewerPageData,
  type ProcessingStatuses,
  type SharedTechnicalDataPackage,
  type TechnicalDataPackage,
} from '@frontend/api';
import type { ChatChannelType } from '@frontend/api';

import type { PartUploaderPayload } from 'components/PartUploader/PartUploader';

import type { ViewerMode } from 'constants/all';

import { LOCATION_CHANGE } from 'store/app/actions';
import type { ActionWithPayload } from 'store/types';

import { notify } from 'utils/reduxUtils';

import type { AppsPanelStateValues, PartPanelStateValues } from './constants';

export const types = {
  GET_TECHNICAL_DATA_PACKAGE_ERROR:
    'partLibrary/viewer/GET_TECHNICAL_DATA_PACKAGE_ERROR',
  PART_DETAILS_TOGGLE_COLLAPSE:
    'partLibrary/viewer/PART_DETAILS_TOGGLE_COLLAPSE',
  PART_DETAILS_TOGGLE_EDITABLE:
    'partLibrary/viewer/PART_DETAILS_TOGGLE_EDITABLE',
  PART_DETAILS_UPDATE_ERROR: 'partLibrary/viewer/PART_DETAILS_UPDATE_ERROR',
  PART_DESCRIPTORS_UPDATE_REQUEST:
    'partLibrary/viewer/PART_DESCRIPTORS_UPDATE_REQUEST',
  PART_MASTER_DETAILS_UPDATE_REQUEST:
    'partLibrary/viewer/PART_MASTER_DETAILS_UPDATE_REQUEST',
  PART_MASTER_DETAILS_UPDATE_SUCCESS:
    'partLibrary/viewer/PART_MASTER_DETAILS_UPDATE_SUCCESS',
  PART_DETAILS_UPDATE_SUCCESS: 'partLibrary/viewer/PART_DETAILS_UPDATE_SUCCESS',
  RECEIVE_PART_FILE_INTERROGATION_STATUSES:
    'partLibrary/viewer/RECEIVE_PART_FILE_INTERROGATION_STATUSES',
  RECEIVE_PART_FILE_PROCESSING_STATUSES:
    'partLibrary/viewer/RECEIVE_PART_FILE_PROCESSING_STATUSES',
  RECEIVE_MULTI_LEVEL_BOM: 'partLibrary/viewer/RECEIVE_MULTI_LEVEL_BOM',
  RECEIVE_TECHNICAL_DATA_PACKAGE:
    'partLibrary/viewer/RECEIVE_TECHNICAL_DATA_PACKAGE',
  REMOVE_SUPPORTING_FILE_ERROR:
    'partLibrary/viewer/REMOVE_SUPPORTING_FILE_ERROR',
  REMOVE_SUPPORTING_FILE_REQUEST:
    'partLibrary/viewer/REMOVE_SUPPORTING_FILE_REQUEST',
  REMOVE_SUPPORTING_FILE_SUCCESS:
    'partLibrary/viewer/REMOVE_SUPPORTING_FILE_SUCCESS',
  PART_DETAILS_REQUEST: 'partLibrary/viewer/PART_DETAILS_REQUEST',

  DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST:
    'partLibrary/viewer/DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST',
  DOWNLOAD_TECHNICAL_DATA_PACKAGE_SUCCESS:
    'partLibrary/viewer/DOWNLOAD_TECHNICAL_DATA_PACKAGE_SUCCESS',
  DOWNLOAD_TECHNICAL_DATA_PACKAGE_ERROR:
    'partLibrary/viewer/DOWNLOAD_TECHNICAL_DATA_PACKAGE_ERROR',
  NOTIFY_PACKAGE_TOO_LARGE: 'partLibrary/viewer/NOTIFY_PACKAGE_TOO_LARGE',
  SET_ACTIVE_FILE: 'partLibrary/viewer/SET_ACTIVE_FILE',
  SET_ACTIVE_UUID: 'partLibrary/viewer/SET_ACTIVE_UUID',
  SET_APPS_PANEL_STATE: 'partLibrary/viewer/SET_APPS_PANEL_STATE',
  SET_IS_APPS_PANEL_COLLAPSED: 'partLibrary/viewer/SET_IS_APPS_PANEL_COLLAPSED',
  SET_PART_PANEL_STATE: 'partLibrary/viewer/SET_PART_PANEL_STATE',
  PART_FILE_GET: 'partLibrary/viewer/PART_FILE_GET',
  UPLOAD_SUPPORTING_FILES_ERROR:
    'partLibrary/viewer/UPLOAD_SUPPORTING_FILES_ERROR',
  UPLOAD_SUPPORTING_FILES_RECEIVE_FILES:
    'partLibrary/viewer/UPLOAD_SUPPORTING_FILES_RECEIVE_FILES',
  UPLOAD_SUPPORTING_FILES_REQUEST:
    'partLibrary/viewer/UPLOAD_SUPPORTING_FILES_REQUEST',
  UPLOAD_SUPPORTING_FILES_SUCCESS:
    'partLibrary/viewer/UPLOAD_SUPPORTING_FILES_SUCCESS',
  VIEWER_PAGE_CLEAR: 'partLibrary/viewer/VIEWER_PAGE_CLEAR',
  VIEWER_PAGE_INIT: 'partLibrary/viewer/VIEWER_PAGE_INIT',
  REFRESH_TECHNICAL_DATA_PACKAGE:
    'partLibrary/viewer/REFRESH_TECHNICAL_DATA_PACKAGE',
  VIEWER_PAGE_SET_MODE: 'partLibrary/viewer/VIEWER_PAGE_SET_MODE',
  REQUEST_ACCESS_TO_PART: 'partLibrary/viewer/REQUEST_ACCESS_TO_PART',
  REQUEST_ACCESS_TO_PART_SUCCESS:
    'partLibrary/viewer/REQUEST_ACCESS_TO_PART_SUCCESS',
  REQUEST_ACCESS_TO_PART_ERROR:
    'partLibrary/viewer/REQUEST_ACCESS_TO_PART_ERROR',
} as const;

export type SetActiveFilePayload = {
  forceFocus?: boolean;
  partFileUuid: string;
  partUuid?: string | null;
};

export type SetActiveViewerFilePayload = {
  file: {
    uuid: string;
    partUuid?: string;
  };
};

export type MultiLevelBom = Record<string, unknown>;

export type PartFileInterrogationStatuses = {
  [partFileUuid: string]: InterrogationStatus;
};

export type PartFileProcessingStatuses = {
  [partFileUuid: string]: ProcessingStatuses;
};

export type ReceiveMultiLevelBomPayload = {
  multiLevelBom: MultiLevelBom;
};

export type TechnicalDataPackagePayload = {
  technicalDataPackage: TechnicalDataPackage;
};

export type ReceiveTechnicalDataPackagePayload = {
  technicalDataPackage:
    | TechnicalDataPackage
    | SharedTechnicalDataPackage
    | ExpiredSharedTechnicalDataPackage;
};

export type ViewerPageSetModePayload = {
  mode: ViewerMode;
};

export type ViewerPageInitPayload = {
  /**
   * A third-party chat the page opened on, from how the user signed up (MSC),
   * or `null` for the part's own chat.
   */
  initialChatChannelType: ChatChannelType | null;
};

export type SetAppsPanelStatePayload = {
  appsPanelState: AppsPanelStateValues;
  meta?: Record<string, unknown> | null;
};

export type SetIsAppsPanelCollapsedPayload = {
  isCollapsed: boolean;
};

export type SetPartPanelStatePayload = {
  partPanelState: PartPanelStateValues;
};

export type PartDetailsRequestPayload = {
  partUuid: string;
};

export type PartDescriptorsUpdateRequestPayload = {
  partDetails: {
    partNumber?: string | null;
    revision?: string | null;
    description?: string | null;
    materialId?: number | null;
    processTag?: string | null;
  };
  partUuid: string;
};

export type PartMasterDetailsUpdateRequestPayload = {
  isExportControlled: boolean;
};

export type DownloadTechnicalDataPackageRequestPayload = {
  partUUID: string;
};

export type RemoveSupportingFileRequestPayload = {
  supportingFileUUID: string;
};

export type RemoveSupportingFileSuccessPayload = {
  supportingFileUUID: string;
  partUUID: string;
};

export type UploadSupportingFilesReceivePayload = {
  supportingFile: PartFileViewerPageData;
  uuid: string;
};

export type UploadSupportingFilesRequestPayload =
  | PartUploaderPayload
  | {
      category?: string;
      file: File | File[];
      groupId?: string;
      redactedFrom?: string | null;
      units?: string;
    };

export type PartFileGetPayload = {
  partFileUuid: string;
  partUuid: string;
};

export type ReceivePartFileInterrogationStatusesPayload = {
  interrogationStatuses: PartFileInterrogationStatuses;
};

export type ReceivePartFileProcessingStatusesPayload = {
  processingStatuses: PartFileProcessingStatuses;
};

export type RequestAccessToPartPayload = {
  partMasterUuid: string | undefined;
  version: string | undefined;
};

export type ViewerPageAction =
  | ActionWithPayload<typeof LOCATION_CHANGE>
  | ActionWithPayload<typeof types.GET_TECHNICAL_DATA_PACKAGE_ERROR, unknown>
  | ActionWithPayload<typeof types.PART_DETAILS_TOGGLE_COLLAPSE>
  | ActionWithPayload<typeof types.PART_DETAILS_TOGGLE_EDITABLE>
  | ActionWithPayload<typeof types.PART_DETAILS_UPDATE_ERROR, unknown>
  | ActionWithPayload<
      typeof types.PART_DESCRIPTORS_UPDATE_REQUEST,
      PartDescriptorsUpdateRequestPayload
    >
  | ActionWithPayload<
      typeof types.PART_MASTER_DETAILS_UPDATE_REQUEST,
      PartMasterDetailsUpdateRequestPayload
    >
  | ActionWithPayload<
      typeof types.PART_MASTER_DETAILS_UPDATE_SUCCESS,
      TechnicalDataPackagePayload
    >
  | ActionWithPayload<
      typeof types.PART_DETAILS_UPDATE_SUCCESS,
      TechnicalDataPackagePayload
    >
  | ActionWithPayload<
      typeof types.RECEIVE_PART_FILE_INTERROGATION_STATUSES,
      ReceivePartFileInterrogationStatusesPayload
    >
  | ActionWithPayload<
      typeof types.RECEIVE_PART_FILE_PROCESSING_STATUSES,
      ReceivePartFileProcessingStatusesPayload
    >
  | ActionWithPayload<
      typeof types.RECEIVE_MULTI_LEVEL_BOM,
      ReceiveMultiLevelBomPayload
    >
  | ActionWithPayload<
      typeof types.RECEIVE_TECHNICAL_DATA_PACKAGE,
      ReceiveTechnicalDataPackagePayload
    >
  | ActionWithPayload<typeof types.REMOVE_SUPPORTING_FILE_ERROR, unknown>
  | ActionWithPayload<
      typeof types.REMOVE_SUPPORTING_FILE_REQUEST,
      RemoveSupportingFileRequestPayload
    >
  | ActionWithPayload<
      typeof types.REMOVE_SUPPORTING_FILE_SUCCESS,
      RemoveSupportingFileSuccessPayload
    >
  | ActionWithPayload<
      typeof types.PART_DETAILS_REQUEST,
      PartDetailsRequestPayload
    >
  | ActionWithPayload<
      typeof types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST,
      DownloadTechnicalDataPackageRequestPayload
    >
  | ActionWithPayload<typeof types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_SUCCESS>
  | ActionWithPayload<
      typeof types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_ERROR,
      unknown
    >
  | ActionWithPayload<typeof types.NOTIFY_PACKAGE_TOO_LARGE, string>
  | ActionWithPayload<typeof types.SET_ACTIVE_FILE, SetActiveViewerFilePayload>
  | ActionWithPayload<typeof types.SET_ACTIVE_UUID, SetActiveFilePayload>
  | ActionWithPayload<
      typeof types.SET_APPS_PANEL_STATE,
      SetAppsPanelStatePayload
    >
  | ActionWithPayload<
      typeof types.SET_IS_APPS_PANEL_COLLAPSED,
      SetIsAppsPanelCollapsedPayload
    >
  | ActionWithPayload<
      typeof types.SET_PART_PANEL_STATE,
      SetPartPanelStatePayload
    >
  | ActionWithPayload<typeof types.PART_FILE_GET, PartFileGetPayload>
  | ActionWithPayload<typeof types.UPLOAD_SUPPORTING_FILES_ERROR, unknown>
  | ActionWithPayload<
      typeof types.UPLOAD_SUPPORTING_FILES_RECEIVE_FILES,
      UploadSupportingFilesReceivePayload
    >
  | ActionWithPayload<
      typeof types.UPLOAD_SUPPORTING_FILES_REQUEST,
      UploadSupportingFilesRequestPayload
    >
  | ActionWithPayload<typeof types.UPLOAD_SUPPORTING_FILES_SUCCESS>
  | ActionWithPayload<typeof types.VIEWER_PAGE_CLEAR>
  | ActionWithPayload<typeof types.VIEWER_PAGE_INIT, ViewerPageInitPayload>
  | ActionWithPayload<typeof types.REFRESH_TECHNICAL_DATA_PACKAGE>
  | ActionWithPayload<
      typeof types.VIEWER_PAGE_SET_MODE,
      ViewerPageSetModePayload
    >
  | ActionWithPayload<
      typeof types.REQUEST_ACCESS_TO_PART,
      RequestAccessToPartPayload
    >
  | ActionWithPayload<typeof types.REQUEST_ACCESS_TO_PART_SUCCESS>
  | ActionWithPayload<typeof types.REQUEST_ACCESS_TO_PART_ERROR>;

export const partLibraryViewerPage = {
  clear: () => ({
    type: types.VIEWER_PAGE_CLEAR,
  }),
  error: notify(
    (error: unknown) => ({
      type: types.GET_TECHNICAL_DATA_PACKAGE_ERROR,
      payload: error,
    }),
    'Failed to fetch part file',
  ),
  /** The page has loaded what it shows; chat may start. */
  init: ({ initialChatChannelType }: ViewerPageInitPayload) => ({
    type: types.VIEWER_PAGE_INIT,
    payload: { initialChatChannelType },
  }),
  /** Refetches the loaded technical data package, keeping the active file. */
  refreshTechnicalDataPackage: () => ({
    type: types.REFRESH_TECHNICAL_DATA_PACKAGE,
  }),
  receiveMultiLevelBom: ({ multiLevelBom }: ReceiveMultiLevelBomPayload) => ({
    type: types.RECEIVE_MULTI_LEVEL_BOM,
    payload: { multiLevelBom },
  }),
  receiveTechnicalDataPackage: ({
    technicalDataPackage,
  }: ReceiveTechnicalDataPackagePayload) => ({
    type: types.RECEIVE_TECHNICAL_DATA_PACKAGE,
    payload: { technicalDataPackage },
  }),
  setActiveFile: ({ file }: SetActiveViewerFilePayload) => ({
    type: types.SET_ACTIVE_FILE,
    payload: { file },
  }),
  setActiveUUID: ({
    forceFocus = true,
    partFileUuid,
    partUuid = null,
  }: SetActiveFilePayload) => ({
    type: types.SET_ACTIVE_UUID,
    payload: { forceFocus, partFileUuid, partUuid },
  }),
  setMode: ({ mode }: ViewerPageSetModePayload) => ({
    type: types.VIEWER_PAGE_SET_MODE,
    payload: { mode },
  }),
  setAppsPanelState: ({ appsPanelState, meta }: SetAppsPanelStatePayload) => ({
    type: types.SET_APPS_PANEL_STATE,
    payload: { appsPanelState, meta },
  }),
  setIsAppsPanelCollapsed: ({
    isCollapsed,
  }: SetIsAppsPanelCollapsedPayload) => ({
    type: types.SET_IS_APPS_PANEL_COLLAPSED,
    payload: { isCollapsed },
  }),
  setPartPanelState: ({ partPanelState }: SetPartPanelStatePayload) => ({
    type: types.SET_PART_PANEL_STATE,
    payload: { partPanelState },
  }),
};

export const partDetails = {
  toggleCollapse: () => ({
    type: types.PART_DETAILS_TOGGLE_COLLAPSE,
  }),
  toggleEditable: () => ({
    type: types.PART_DETAILS_TOGGLE_EDITABLE,
  }),
  updateError: notify(
    (error: unknown) => ({
      type: types.PART_DETAILS_UPDATE_ERROR,
      payload: error,
    }),
    'Failed to update part details',
  ),
  get: ({ partUuid }: PartDetailsRequestPayload) => ({
    type: types.PART_DETAILS_REQUEST,
    payload: {
      partUuid,
    },
  }),
  updateDescriptorsRequest: ({
    partDetails,
    partUuid,
  }: PartDescriptorsUpdateRequestPayload) => ({
    type: types.PART_DESCRIPTORS_UPDATE_REQUEST,
    payload: {
      partUuid,
      partDetails,
    },
  }),
  updateSuccess: (technicalDataPackage: TechnicalDataPackage) => ({
    type: types.PART_DETAILS_UPDATE_SUCCESS,
    payload: { technicalDataPackage },
  }),
  updatePartMasterRequest: ({
    isExportControlled,
  }: PartMasterDetailsUpdateRequestPayload) => ({
    type: types.PART_MASTER_DETAILS_UPDATE_REQUEST,
    payload: { isExportControlled },
  }),
  updatePartMasterSuccess: (technicalDataPackage: TechnicalDataPackage) => ({
    type: types.PART_MASTER_DETAILS_UPDATE_SUCCESS,
    payload: { technicalDataPackage },
  }),
};

export const downloadTechnicalDataPackage = {
  request: notify(
    ({ partUUID }: DownloadTechnicalDataPackageRequestPayload) => ({
      type: types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST,
      payload: { partUUID },
    }),
    'Please wait. Preparing for download...',
  ),
  success: notify(
    () => ({
      type: types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_SUCCESS,
    }),
    'Download started',
  ),
  error: notify(
    (error: unknown) => ({
      type: types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_ERROR,
      payload: error,
    }),
    'Failed to download technical data package',
  ),
};

export const notifyPackageTooLarge = notify(
  (url: string) => ({
    type: types.NOTIFY_PACKAGE_TOO_LARGE,
    payload: url,
  }),
  'Preparing the ZIP file for these files might take a while. We will email you the link',
);

export const removeSupportingFile = {
  error: notify(
    (error: unknown) => ({
      type: types.REMOVE_SUPPORTING_FILE_ERROR,
      payload: error,
    }),
    'Failed to fetch part file',
  ),
  request: ({ supportingFileUUID }: RemoveSupportingFileRequestPayload) => ({
    type: types.REMOVE_SUPPORTING_FILE_REQUEST,
    payload: { supportingFileUUID },
  }),
  success: ({
    supportingFileUUID,
    partUUID,
  }: RemoveSupportingFileSuccessPayload) => ({
    type: types.REMOVE_SUPPORTING_FILE_SUCCESS,
    payload: { supportingFileUUID, partUUID },
  }),
};

export const uploadSupportingFiles = {
  error: notify(
    (error: unknown) => ({
      type: types.UPLOAD_SUPPORTING_FILES_ERROR,
      payload: error,
    }),
    'Failed to upload supporting files',
  ),
  receiveFile: ({
    supportingFile,
    uuid,
  }: UploadSupportingFilesReceivePayload) => ({
    type: types.UPLOAD_SUPPORTING_FILES_RECEIVE_FILES,
    payload: { supportingFile, uuid },
  }),
  request: (payload: UploadSupportingFilesRequestPayload) => ({
    type: types.UPLOAD_SUPPORTING_FILES_REQUEST,
    payload,
  }),
  success: () => ({
    type: types.UPLOAD_SUPPORTING_FILES_SUCCESS,
  }),
};

// NOTE: Do not use this with imputinity, it is very inefficient since it fetches the entire
//  tech data package to refresh the part file.
export const partFile = {
  get: ({ partFileUuid, partUuid }: PartFileGetPayload) => ({
    type: types.PART_FILE_GET,
    payload: { partFileUuid, partUuid },
  }),
};

export const partFileStatuses = {
  receiveInterrogationStatuses: ({
    interrogationStatuses,
  }: ReceivePartFileInterrogationStatusesPayload) => ({
    type: types.RECEIVE_PART_FILE_INTERROGATION_STATUSES,
    payload: { interrogationStatuses },
  }),
  receiveProcessingStatuses: ({
    processingStatuses,
  }: ReceivePartFileProcessingStatusesPayload) => ({
    type: types.RECEIVE_PART_FILE_PROCESSING_STATUSES,
    payload: { processingStatuses },
  }),
};

export const requestAccessToSharedTechDataPackage = {
  request: ({ partMasterUuid, version }: RequestAccessToPartPayload) => ({
    type: types.REQUEST_ACCESS_TO_PART,
    payload: { partMasterUuid, version },
  }),
  success: notify(
    () => ({
      type: types.REQUEST_ACCESS_TO_PART_SUCCESS,
    }),
    'Request for access sent to part owner',
  ),
  error: notify(
    () => ({
      type: types.REQUEST_ACCESS_TO_PART_ERROR,
    }),
    'Error occurred while sending request for access',
  ),
};
